import { useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion, type Variants } from 'framer-motion'
import {
  DASHBOARD_NAV_GROUPS,
  DASHBOARD_NAV_TOP,
  type DashboardNavItem
} from '../../../constants/dashboard-nav.constants'
import { useAuth } from '../../../context/AuthContext'
import { StorefrontBackdrop } from '../storefront/chrome'

/**
 * Visibility rule: screen-coded entries need `view`; anything without a
 * code (none today) shows. Every operator screen has a code, so the nav
 * is driven purely by Layer-2c grants — admin included.
 */
function navItemVisible(
  item: DashboardNavItem,
  can: (screen: string, action: 'view') => boolean
): boolean {
  if (item.screen !== undefined) return can(item.screen, 'view')
  return true
}

/**
 * OperatorSidebar — the shared operator navigation, as a left slide-in drawer.
 *
 * NOT dashboard-specific: it renders inside the operator shell on EVERY operator
 * screen. It's always hidden and opens only when the top bar's menu button is
 * tapped; clicking the dim outside closes it (Kiran 2026-05-21). Same warm
 * "story" theme — background image + dark copy — and it slides in left→right.
 *
 * Entries come from DASHBOARD_NAV_TOP + DASHBOARD_NAV_GROUPS (Rule #14).
 */
export function OperatorSidebar({ drawerOpen }: { readonly drawerOpen: boolean }): JSX.Element {
  const location = useLocation()
  const reduce = useReducedMotion() === true
  const { can } = useAuth()

  const findActiveGroup = (path: string): string | null => {
    for (const g of DASHBOARD_NAV_GROUPS) {
      for (const it of g.items) {
        if (it.to === path) return g.id
        if ((it.end ?? false) === false && path.startsWith(it.to + '/')) return g.id
      }
    }
    return null
  }

  const [openMap, setOpenMap] = useState<Record<string, boolean>>(() => {
    const active = findActiveGroup(location.pathname)
    const initial: Record<string, boolean> = {}
    for (const g of DASHBOARD_NAV_GROUPS) initial[g.id] = g.id === active
    return initial
  })

  useEffect(() => {
    const active = findActiveGroup(location.pathname)
    if (active === null) return
    setOpenMap((prev) => (prev[active] ? prev : { ...prev, [active]: true }))
  }, [location.pathname])

  const toggle = (id: string): void => {
    setOpenMap((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const listV: Variants = {
    show: { transition: { staggerChildren: reduce ? 0 : 0.05, delayChildren: reduce ? 0 : 0.08 } }
  }
  const itemV: Variants = {
    hidden: { opacity: reduce ? 1 : 0, x: reduce ? 0 : -10 },
    show: { opacity: 1, x: 0, transition: { duration: 0.3, ease: 'easeOut' } }
  }

  const itemClass = ({ isActive }: { isActive: boolean }): string =>
    `relative flex items-center justify-between gap-2 rounded-md px-3 py-2.5 transition-all duration-200 ${
      isActive
        ? 'text-text-light font-semibold'
        : 'text-text-light hover:translate-x-1 hover:bg-text-light/10'
    }`

  const activePill = (isActive: boolean): JSX.Element | null =>
    isActive ? (
      <motion.span
        layoutId="opActive"
        className="absolute inset-0 z-0 rounded-md border-l-2 border-forest bg-text-light/15"
        transition={reduce ? { duration: 0 } : { type: 'spring', stiffness: 380, damping: 30 }}
      />
    ) : null

  return (
    <aside
      aria-hidden={!drawerOpen}
      className={`fixed bottom-0 left-0 top-20 z-40 w-72 overflow-hidden shadow-2xl transition-transform duration-300 ease-out ${
        drawerOpen ? 'translate-x-0' : '-translate-x-full'
      }`}
    >
      <StorefrontBackdrop />
      {/* The background image is the drawer's backdrop; the nav section sits on a
          walnut panel for contrast against the cream content (Kiran 2026-05-21). */}
      <div className="absolute inset-3 z-10 overflow-hidden rounded-2xl bg-gradient-to-b from-bg-dark to-charcoal text-text-light shadow-lg">
        <motion.nav
          className="h-full overflow-y-auto px-3 py-5"
          initial="hidden"
          animate={drawerOpen ? 'show' : 'hidden'}
          variants={listV}
        >
        <ul className="mb-4 space-y-1 font-body text-body-sm">
          {DASHBOARD_NAV_TOP.map((item) => (
            <motion.li key={item.to} variants={itemV}>
              <NavLink to={item.to} end={item.end ?? false} className={itemClass}>
                {({ isActive }) => (
                  <>
                    {activePill(isActive)}
                    <span className="relative z-10">{item.label}</span>
                  </>
                )}
              </NavLink>
            </motion.li>
          ))}
        </ul>
        <ul className="space-y-2 font-body text-body-sm">
          {DASHBOARD_NAV_GROUPS.map((group) => {
            const items = group.items.filter((it) => navItemVisible(it, can))
            if (items.length === 0) return null
            const isOpen = openMap[group.id] === true
            return (
              <motion.li key={group.id} variants={itemV}>
                <button
                  type="button"
                  onClick={() => toggle(group.id)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-text-light transition hover:bg-text-light/10"
                >
                  <span className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-forest" aria-hidden />
                    <span className="font-heading text-micro uppercase tracking-wider">{group.label}</span>
                  </span>
                  <Chevron open={isOpen} />
                </button>
                <AnimatePresence initial={false}>
                  {isOpen ? (
                    <motion.ul
                      key="items"
                      className="ml-3 mt-1 space-y-1 overflow-hidden border-l border-text-light/10 pl-2"
                      initial={reduce ? false : { height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: 'easeOut' }}
                    >
                      {items.map((item) => (
                        <li key={item.to}>
                          <NavLink to={item.to} end={item.end ?? false} className={itemClass}>
                            {({ isActive }) => (
                              <>
                                {activePill(isActive)}
                                <span className="relative z-10">{item.label}</span>
                              </>
                            )}
                          </NavLink>
                        </li>
                      ))}
                    </motion.ul>
                  ) : null}
                </AnimatePresence>
              </motion.li>
            )
          })}
        </ul>
        </motion.nav>
      </div>
    </aside>
  )
}

function Chevron({ open }: { readonly open: boolean }): JSX.Element {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden
      className={`transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
    >
      <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}
